import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  IDtoSiteBlackoutTimes,
  IDtoSiteDetails,
  IDtoSiteIsolation,
  IDtoSiteName,
  IDtoSitePrice,
  IPhoto,
  ISite,
  ISiteWhere,
  IVehicle,
} from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';
import { BASE_API_URL } from '../network/api-config';

const API_URL = BASE_API_URL + 'site';

/**
 * Local fork of @curbnturf/site's SiteService — trimmed to plain HttpClient
 * and to the methods actually called. create/createAmenity/restore/
 * removeAmenity/upsert/generic update/updateActivities/updateAmenities/
 * updateSiteName/updateNoiseAndPrivacy/updateAccessRoutes/uploadImages/
 * favorite/unfavorite/retrieveLocalOwnedSites (native SQLite) aren't used.
 */
@Injectable({ providedIn: 'root' })
export class SiteApiService {
  private readonly http = inject(HttpClient);

  fetch(siteId: number): Observable<ISite> {
    return this.http.get<ISite>(`${API_URL}/${siteId}`);
  }

  fetchAll(
    query: ISiteWhere,
    sort = 'id',
    page = 1,
    pageSize = 100,
  ): Observable<{ total: number; page: number; sites: ISite[] }> {
    const queryString = objectToUrlParams({ ...query, sort, page, pageSize });
    return this.http.get<{ total: number; page: number; sites: ISite[] }>(`${API_URL}?${queryString}`);
  }

  /** `listingDisabled` (with a `message`) means this was the listing's last available site, so the listing was unpublished too. */
  remove(siteId: number): Observable<ISite & { listingDisabled?: boolean; message?: string }> {
    return this.http.delete<ISite & { listingDisabled?: boolean; message?: string }>(`${API_URL}/${siteId}`);
  }

  updateDetails(siteId: number, details: IDtoSiteDetails): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/details`, this.formatForServer(details));
  }

  updateIsolation(siteId: number, data: IDtoSiteIsolation): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/isolation`, data);
  }

  updateName(siteId: number, data: IDtoSiteName): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/name`, data);
  }

  updateVehicles(siteId: number, data: IVehicle): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/vehicles`, data);
  }

  updatePrice(siteId: number, data: IDtoSitePrice): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/price`, data);
  }

  updateBlackoutTimes(siteId: number, data: IDtoSiteBlackoutTimes): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/blackout-times`, data);
  }

  updateSitePhotos(siteId: number, photos: IPhoto[]): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/photo-details`, photos);
  }

  addPhotoFromListing(siteId: number, key: string): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.post<ISite>(`${API_URL}/${siteId}/add-photo-from-listing`, { key });
  }

  duplicateSite(siteId: number, name: string): Observable<ISite> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.put<ISite>(`${API_URL}/${siteId}/duplicate`, { name });
  }

  enable(site: ISite): Observable<{ success: boolean; message: string; listingDisabled?: boolean }> {
    if (!site.id) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.post<{ success: boolean; message: string; listingDisabled?: boolean }>(
      `${API_URL}/${site.id}/enable`,
      null,
    );
  }

  disable(site: ISite): Observable<{ success: boolean; message: string; listingDisabled?: boolean }> {
    if (!site.id) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.post<{ success: boolean; message: string; listingDisabled?: boolean }>(
      `${API_URL}/${site.id}/disable`,
      null,
    );
  }

  removeImage(siteId: number, photoIndex: number): Observable<IPhoto[]> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.delete<IPhoto[]>(`${API_URL}/${siteId}/remove-image/${photoIndex}`);
  }

  /** Removes the photo with this key; 404 if it's no longer there. Prefer this to removeImage's position. */
  removeImageByKey(siteId: number, key: string): Observable<IPhoto[]> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.delete<IPhoto[]>(`${API_URL}/${siteId}/remove-image`, { params: { key } });
  }

  uploadImage(siteId: number, formData: FormData): Observable<{ id: number; photos: IPhoto[] }> {
    if (!siteId) {
      throw new Error('Missing Site Identifier');
    }
    return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${siteId}/upload-image`, formData);
  }

  private formatForServer<T extends object>(site: T): T {
    return { ...site };
  }
}
